import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { InventoryListItem } from '../components/InventoryListItem';
import { WantListItem } from '../components/WantListItem';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Package,
  Eye,
  EyeOff,
  CheckCircle,
  Loader2,
  DollarSign,
  Clock,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
  Tag,
  PauseCircle,
  AlertTriangle,
  ShoppingBag,
  Search,
  XCircle,
  Sparkles,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import { formatLargeNumber } from '../lib/utils';

// Tab filter options
const ITEM_TABS = [
  { id: 'active', label: 'Active', icon: Eye },
  { id: 'unlisted', label: 'Unlisted', icon: EyeOff },
  { id: 'sold', label: 'Sold', icon: CheckCircle },
  { id: 'all', label: 'All', icon: Package },
];

const WANT_TABS = [
  { id: 'active', label: 'Active', icon: ShoppingBag },
  { id: 'closed', label: 'Old Wants', icon: XCircle },
  { id: 'all', label: 'All', icon: Search },
];

// Helper: time remaining until expires_at
function timeRemaining(expiresAt) {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  return `${hours}h ${mins}m`;
}

// Dashboard-level pending offers summary (fetches once, groups by item)
function PendingOffersSummary({ offers, onRespond }) {
  const [expanded, setExpanded] = useState(true);
  const [counterValues, setCounterValues] = useState({});
  const [showCounterInput, setShowCounterInput] = useState({});

  if (!offers || offers.length === 0) return null;

  const byItem = offers.reduce((acc, o) => {
    const key = o.item_id;
    if (!acc[key]) acc[key] = { item_title: o.item_title || 'Item', offers: [] };
    acc[key].offers.push(o);
    return acc;
  }, {});

  const handleRespond = async (offerId, action, counterAmount) => {
    const body = { action };
    if (action === 'counter') {
      const cents = Math.round(parseFloat(counterAmount) * 100);
      if (!cents || cents <= 0) { toast.error('Enter a valid counter amount'); return; }
      body.counter_amount_cents = cents;
    }
    const ok = await onRespond(offerId, body);
    if (ok) setShowCounterInput(prev => ({ ...prev, [offerId]: false }));
  };

  return (
    <div className="mb-6 rounded-2xl border border-[#C9956C]/40 bg-[#C9956C]/5 overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-[#C9956C] hover:bg-[#C9956C]/10 transition-colors"
      >
        <span className="flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          {offers.length} offer{offers.length !== 1 ? 's' : ''} pending across {Object.keys(byItem).length} item{Object.keys(byItem).length !== 1 ? 's' : ''}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-[#C9956C]/20 pt-4">
          {Object.entries(byItem).map(([itemId, group]) => (
            <div key={itemId}>
              <Link
                to={`/item/${itemId}`}
                className="text-sm font-semibold text-[var(--theme-text-primary)] hover:text-[#C9956C] transition-colors inline-block mb-2"
              >
                {group.item_title}
              </Link>
              <div className="space-y-2">
                {group.offers.map(offer => (
                  <div key={offer.id} className="rounded-lg bg-[var(--theme-card)] border border-[var(--theme-border)] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-semibold text-[var(--theme-text-primary)]">
                          ${(offer.amount_cents / 100).toFixed(2)}
                        </span>
                        <span className="text-sm text-[var(--theme-text-secondary)] ml-2">
                          from {offer.buyer_username || 'buyer'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[var(--theme-text-muted)]">
                        <Clock className="w-3 h-3" />
                        {timeRemaining(offer.expires_at)}
                      </div>
                    </div>

                    {offer.status === 'accepted' ? (
                      <p className="text-xs text-[#059669] font-semibold">✓ Accepted — waiting for buyer payment</p>
                    ) : showCounterInput[offer.id] ? (
                      <div className="flex gap-2 mt-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-secondary)] text-sm">$</span>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="Counter amount"
                            value={counterValues[offer.id] || ''}
                            onChange={e => setCounterValues(prev => ({ ...prev, [offer.id]: e.target.value }))}
                            className="pl-6 h-9 text-sm rounded-lg"
                            autoFocus
                          />
                        </div>
                        <Button
                          onClick={() => handleRespond(offer.id, 'counter', counterValues[offer.id])}
                          className="h-9 px-3 text-xs bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-lg"
                        >
                          Send
                        </Button>
                        <button
                          onClick={() => setShowCounterInput(prev => ({ ...prev, [offer.id]: false }))}
                          className="h-9 px-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={() => handleRespond(offer.id, 'accept')}
                          className="h-8 px-3 text-xs bg-[#059669] hover:bg-[#047857] text-white rounded-lg flex-1"
                        >
                          Accept
                        </Button>
                        <Button
                          onClick={() => setShowCounterInput(prev => ({ ...prev, [offer.id]: true }))}
                          className="h-8 px-3 text-xs bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-lg flex-1"
                        >
                          Counter
                        </Button>
                        <Button
                          onClick={() => handleRespond(offer.id, 'decline')}
                          className="h-8 px-3 text-xs bg-transparent border border-red-400 text-red-500 hover:bg-red-50 rounded-lg flex-1"
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OwnerDashboard() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [myInventory, setMyInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCloseWantId, setConfirmCloseWantId] = useState(null);
  const [closeWantLoading, setCloseWantLoading] = useState(false);
  const [selectedWantIds, setSelectedWantIds] = useState(new Set());
  const [bulkWantLoading, setBulkWantLoading] = useState(false);
  const [confirmBulkCloseWants, setConfirmBulkCloseWants] = useState(false);
  const [confirmBulkDeleteWants, setConfirmBulkDeleteWants] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'marketplace' | 'auto' | 'pets'

  // Wants section state
  const [activeSection, setActiveSection] = useState('items'); // 'items' | 'wants'
  const [myWants, setMyWants] = useState([]);
  const [wantsLoading, setWantsLoading] = useState(false);
  const [activeWantTab, setActiveWantTab] = useState('active');
  const [wantsFetched, setWantsFetched] = useState(false);

  useEffect(() => {
    fetchData();
    fetchOffers();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use the new endpoint that includes match counts and previews
      const res = await api.get('/inventory/my/with-matches');
      setMyInventory(res.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load your items');
    } finally {
      setLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      const res = await api.get('/offers/my');
      const active = (res.data.as_seller || []).filter(o =>
        ['pending', 'countered', 'accepted'].includes(o.status)
      );
      setPendingOffers(active);
    } catch (e) {
      // silently ignore — dashboard still renders
    }
  };

  const fetchWants = async () => {
    setWantsLoading(true);
    try {
      const res = await api.get('/wtb/my/with-matches');
      setMyWants(res.data);
      setWantsFetched(true);
    } catch (error) {
      toast.error('Failed to load your wants');
    } finally {
      setWantsLoading(false);
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === 'wants' && !wantsFetched) {
      fetchWants();
    }
  };

  const handleCloseWant = async () => {
    if (!confirmCloseWantId) return;
    setCloseWantLoading(true);
    try {
      await api.post(`/wtb/${confirmCloseWantId}/close`);
      toast.success('Removed from your wants');
      setConfirmCloseWantId(null);
      fetchWants();
    } catch {
      toast.error('Failed to update want');
    } finally {
      setCloseWantLoading(false);
    }
  };

  const handleReopenWant = async (listingId) => {
    try {
      await api.post(`/wtb/${listingId}/reopen`);
      toast.success('Want reopened successfully');
      fetchWants();
    } catch {
      toast.error('Failed to reopen want');
    }
  };

  const toggleWantSelect = (id) => {
    setSelectedWantIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkCloseWants = async () => {
    if (selectedWantIds.size === 0) return;
    setBulkWantLoading(true);
    try {
      await Promise.all(
        Array.from(selectedWantIds).map(id => api.post(`/wtb/${id}/close`)),
      );
      toast.success(`${selectedWantIds.size} want${selectedWantIds.size !== 1 ? 's' : ''} marked as no longer wanted`);
      setSelectedWantIds(new Set());
      setConfirmBulkCloseWants(false);
      fetchWants();
    } catch {
      toast.error('Some wants could not be updated');
    } finally {
      setBulkWantLoading(false);
    }
  };

  const handleBulkDeleteWants = async () => {
    if (selectedWantIds.size === 0) return;
    setBulkWantLoading(true);
    try {
      await Promise.all(
        Array.from(selectedWantIds).map(id => api.delete(`/wtb/${id}`)),
      );
      toast.success(`${selectedWantIds.size} want${selectedWantIds.size !== 1 ? 's' : ''} deleted`);
      setSelectedWantIds(new Set());
      setConfirmBulkDeleteWants(false);
      fetchWants();
    } catch {
      toast.error('Some wants could not be deleted');
    } finally {
      setBulkWantLoading(false);
    }
  };

  const handleBulkReopenWants = async () => {
    if (selectedWantIds.size === 0) return;
    setBulkWantLoading(true);
    try {
      await Promise.all(
        Array.from(selectedWantIds).map(id => api.post(`/wtb/${id}/reopen`)),
      );
      toast.success(`${selectedWantIds.size} want${selectedWantIds.size !== 1 ? 's' : ''} reopened`);
      setSelectedWantIds(new Set());
      fetchWants();
    } catch {
      toast.error('Some wants could not be reopened');
    } finally {
      setBulkWantLoading(false);
    }
  };

  const handleRespondOffer = async (offerId, body) => {
    try {
      await api.post(`/offers/${offerId}/respond`, body);
      toast.success(
        body.action === 'accept' ? 'Offer accepted!' :
        body.action === 'decline' ? 'Offer declined' : 'Counter sent!'
      );
      fetchOffers();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
      return false;
    }
  };

  const handleItemClick = (itemId) => {
    navigate(`/item/${itemId}`);
  };

  const handleMarkSold = async (itemId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/inventory/${itemId}/mark-sold`);
      toast.success('Item marked as sold!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as sold');
    }
  };

  const handleMarkClaimed = async (itemId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/inventory/${itemId}/mark-claimed`);
      toast.success('Item marked as claimed!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as claimed');
    }
  };

  const handleRelist = async (itemId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/inventory/${itemId}/relist`);
      toast.success('Item relisted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to relist item');
    }
  };

  // --- Multi-select handlers ---
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedIds(new Set());
  };

  const handleToggleSelect = (itemId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectable = filteredItems.filter(i => i.status !== 'sold' && i.status !== 'banned');
    if (selectable.length > 0 && selectable.every(i => selectedIds.has(i.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map(i => i.id)));
    }
  };

  const handleBulkVisibility = async (isPublic) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/inventory/bulk-visibility', {
        item_ids: Array.from(selectedIds),
        is_public: isPublic
      });
      const action = isPublic ? 'listed' : 'unlisted';
      toast.success(`${res.data.modified_count} item${res.data.modified_count !== 1 ? 's' : ''} marked as ${action}`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update items');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/inventory/bulk-delete', {
        item_ids: Array.from(selectedIds)
      });
      toast.success(`${res.data.deleted_count} item${res.data.deleted_count !== 1 ? 's' : ''} deleted`);
      setSelectedIds(new Set());
      setConfirmDelete(false);
      fetchData();
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete items');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkMarkSold = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/inventory/bulk-mark-sold', {
        item_ids: Array.from(selectedIds)
      });
      toast.success(`${res.data.modified_count} item${res.data.modified_count !== 1 ? 's' : ''} marked as sold`);
      setSelectedIds(new Set());
      fetchData();
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark items sold');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkMarkHold = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/inventory/bulk-mark-hold', {
        item_ids: Array.from(selectedIds)
      });
      toast.success(`${res.data.modified_count} item${res.data.modified_count !== 1 ? 's' : ''} placed on hold`);
      setSelectedIds(new Set());
      fetchData();
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place items on hold');
    } finally {
      setBulkLoading(false);
    }
  };

  // Support both 'active' and 'available' statuses for backwards compatibility
  const applyCategoryFilter = (items) => {
    if (categoryFilter === 'marketplace') return items.filter(i => i.category !== 'vehicles' && i.category !== 'live_animals');
    if (categoryFilter === 'auto') return items.filter(i => i.category === 'vehicles');
    if (categoryFilter === 'pets') return items.filter(i => i.category === 'live_animals');
    return items;
  };

  const availableItems = applyCategoryFilter(myInventory.filter(i => i.status === 'available' || i.status === 'active'));
  const publicItems = availableItems.filter(i => i.is_public);
  const passiveItems = availableItems.filter(i => !i.is_public);
  const soldItems = applyCategoryFilter(myInventory.filter(i => i.status === 'sold'));

  // Filter items based on active tab
  const getFilteredItems = () => {
    switch (activeTab) {
      case 'active':
        return publicItems;
      case 'unlisted':
        return passiveItems;
      case 'sold':
        return soldItems;
      case 'all':
      default:
        return applyCategoryFilter(myInventory);
    }
  };

  const filteredItems = getFilteredItems();

  // Selection computed values
  const selectableItems = filteredItems.filter(i => i.status !== 'sold' && i.status !== 'banned');
  const allSelectableSelected = selectableItems.length > 0 && selectableItems.every(i => selectedIds.has(i.id));
  const someSelected = selectedIds.size > 0;

  // Wants computed values
  const filteredWants = myWants.filter(l => {
    if (activeWantTab === 'active') return l.status !== 'closed';
    if (activeWantTab === 'closed') return l.status === 'closed';
    return true;
  });
  const wantsStats = {
    active: myWants.filter(l => l.status !== 'closed').length,
    closed: myWants.filter(l => l.status === 'closed').length,
  };
  const getWantTabCount = (tabId) => {
    if (tabId === 'active') return wantsStats.active;
    if (tabId === 'closed') return wantsStats.closed;
    return myWants.length;
  };

  // Get tab counts
  const getTabCount = (tabId) => {
    if (tabId === 'active') return publicItems.length;
    if (tabId === 'unlisted') return passiveItems.length;
    if (tabId === 'sold') return soldItems.length;
    return myInventory.length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="owner-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">My Items</h1>
            <p className="text-sm text-[var(--theme-text-muted)]">Manage your listings and wants</p>
          </div>
          {activeSection === 'items' ? (
            <Link to={categoryFilter === 'auto' ? '/auto/cars/list' : categoryFilter === 'pets' ? '/pets/list' : '/list'}>
              <Button className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full" data-testid="add-inventory-btn">
                <Plus className="w-4 h-4 mr-2" />
                {categoryFilter === 'auto' ? 'List Vehicle' : categoryFilter === 'pets' ? 'List Pet' : 'List Item'}
              </Button>
            </Link>
          ) : (
            <Link to="/post-want">
              <Button className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Post a Want
              </Button>
            </Link>
          )}
        </div>

        {/* Section Switcher */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => handleSectionChange('items')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
              activeSection === 'items'
                ? 'bg-[#1E3A5F] text-white'
                : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
            }`}
          >
            <Package className="w-4 h-4" />
            Items
          </button>
          <button
            onClick={() => handleSectionChange('wants')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
              activeSection === 'wants'
                ? 'bg-[#1E3A5F] text-white'
                : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            Wants
          </button>
        </div>

        {/* ── ITEMS SECTION ── */}
        {activeSection === 'items' && (<>

        {/* Category filter tabs */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto">
          {[
            { id: 'all', label: 'All' },
            { id: 'marketplace', label: 'Marketplace' },
            { id: 'auto', label: 'WANTD Auto' },
            { id: 'pets', label: 'WANTD Pets' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setCategoryFilter(tab.id); setActiveTab('active'); setSelectedIds(new Set()); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                categoryFilter === tab.id
                  ? 'bg-[#C9956C] text-white'
                  : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Filters */}
        <div className="flex items-center gap-2 mb-6 border-b border-[var(--theme-border)] pb-4">
          {ITEM_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const count = getTabCount(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  isActive ? 'bg-white/20' : 'bg-[var(--theme-border)]'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Bulk Action Bar */}
        {someSelected && (
          <div className="sticky top-16 z-20 mb-4 p-3 rounded-xl bg-[#1E3A5F] text-white flex items-center justify-between gap-4 shadow-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-white/70 hover:text-white underline"
              >
                Deselect all
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                onClick={() => handleBulkVisibility(true)}
                disabled={bulkLoading}
                className="bg-[#059669] hover:bg-[#047857] text-white text-xs rounded-full px-4 py-1.5 h-auto"
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                Mark Listed
              </Button>
              <Button
                onClick={() => handleBulkVisibility(false)}
                disabled={bulkLoading}
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs rounded-full px-4 py-1.5 h-auto"
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                Mark Unlisted
              </Button>
              <Button
                onClick={handleBulkMarkSold}
                disabled={bulkLoading}
                className="bg-[#C9956C] hover:bg-[#B8845B] text-white text-xs rounded-full px-4 py-1.5 h-auto"
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Tag className="w-3 h-3 mr-1" />}
                Mark Sold
              </Button>
              <Button
                onClick={handleBulkMarkHold}
                disabled={bulkLoading}
                className="bg-[#EAB308] hover:bg-[#CA8A04] text-white text-xs rounded-full px-4 py-1.5 h-auto"
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PauseCircle className="w-3 h-3 mr-1" />}
                Mark On Hold
              </Button>
              <Button
                onClick={() => setConfirmDelete(true)}
                disabled={bulkLoading}
                className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-full px-4 py-1.5 h-auto"
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        )}

        {/* Delete confirmation dialog */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="max-w-md w-full mx-4 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Delete {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}?</h2>
                  <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                    This cannot be undone. Associated matches and saved references will also be removed.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setConfirmDelete(false)}
                  disabled={bulkLoading}
                  className="bg-transparent border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] rounded-full px-4"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full px-4"
                >
                  {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Pending offers summary (across all items) */}
        <PendingOffersSummary offers={pendingOffers} onRespond={handleRespondOffer} />

        {/* Items List */}
        {filteredItems.length > 0 ? (
          <div className="space-y-4" data-testid="my-inventory-list">
            {/* Select All Row */}
            {selectableItems.length > 1 && (
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                <Checkbox
                  checked={allSelectableSelected}
                  onCheckedChange={handleSelectAll}
                  className="h-5 w-5 rounded border-2 border-[var(--theme-border-hover)] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F] data-[state=checked]:text-white"
                />
                <span className="text-sm text-[var(--theme-text-secondary)]">
                  {allSelectableSelected ? 'Deselect all' : 'Select all'} ({selectableItems.length} items)
                </span>
              </div>
            )}

            {filteredItems.map(item => (
              <InventoryListItem
                key={item.id}
                item={item}
                onClick={() => handleItemClick(item.id)}
                onMarkSold={item.status !== 'sold' && item.status !== 'claimed' ? (e) => handleMarkSold(item.id, e) : null}
                onMarkClaimed={item.is_free && item.status !== 'claimed' && item.status !== 'sold' ? (e) => handleMarkClaimed(item.id, e) : null}
                onRelist={['sold', 'claimed', 'expired'].includes(item.status) ? (e) => handleRelist(item.id, e) : null}
                isSelected={selectedIds.has(item.id)}
                onToggleSelect={item.status !== 'sold' && item.status !== 'banned' ? () => handleToggleSelect(item.id) : null}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] shadow-card">
            <div className="w-16 h-16 rounded-full bg-[#1E3A5F]/10 dark:bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-4">
              {activeTab === 'sold' ? (
                <CheckCircle className="w-8 h-8 text-[#1E3A5F] dark:text-[#C9956C]" />
              ) : activeTab === 'unlisted' ? (
                <EyeOff className="w-8 h-8 text-[#1E3A5F] dark:text-[#C9956C]" />
              ) : (
                <Package className="w-8 h-8 text-[#1E3A5F] dark:text-[#C9956C]" />
              )}
            </div>
            <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
              {activeTab === 'sold' ? 'No Sold Items' : activeTab === 'unlisted' ? 'No Unlisted Items' : 'No Items Yet'}
            </h3>
            <p className="text-[var(--theme-text-secondary)] mb-6 max-w-md mx-auto">
              {activeTab === 'sold' 
                ? "Items you've sold will appear here."
                : activeTab === 'unlisted'
                ? "Unlisted items are matched with buyers but not publicly visible."
                : "List items you're willing to sell. Our system will notify you when someone wants what you have!"}
            </p>
            {activeTab !== 'sold' && (
              <Link to="/list">
                <Button className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  List Your First Item
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Unlisted Items Explanation */}
        <section className="mt-12 p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center flex-shrink-0">
              <EyeOff className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[var(--theme-text-primary)] mb-2">What are Unlisted Items?</h3>
              <p className="text-[var(--theme-text-secondary)] text-sm leading-relaxed">
                Unlisted items are not visible in the marketplace, but our matching system will connect you
                with buyers whose Wants match your items. Perfect for things you'd sell for the right offer.
                You don't have to actively manage listings - just add things you might be willing to sell,
                and we'll notify you when there's interest.
              </p>
            </div>
          </div>
        </section>

        </>)}

        {/* ── WANTS SECTION ── */}
        {activeSection === 'wants' && (
          wantsLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
            </div>
          ) : (<>

          {/* Wants Tab Filters */}
          <div className="flex items-center gap-2 mb-6 border-b border-[var(--theme-border)] pb-4">
            {WANT_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeWantTab === tab.id;
              const count = getWantTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveWantTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`px-1.5 py-0.5 rounded text-xs ${isActive ? 'bg-white/20' : 'bg-[var(--theme-border)]'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Bulk Actions Bar — appears when at least one want is selected */}
          {selectedWantIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl bg-[#1E3A5F]/5 dark:bg-[#C9956C]/5 border border-[#1E3A5F]/20 dark:border-[#C9956C]/20">
              <span className="text-sm font-medium text-[var(--theme-text-primary)] mr-2">
                {selectedWantIds.size} selected
              </span>
              {activeWantTab !== 'closed' && (
                <Button
                  size="sm"
                  onClick={() => setConfirmBulkCloseWants(true)}
                  disabled={bulkWantLoading}
                  className="bg-[#6B7280] hover:bg-[#4A5568] text-white rounded-full px-3 h-8 text-xs"
                >
                  {bulkWantLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />}
                  No longer wanted
                </Button>
              )}
              {activeWantTab === 'closed' && (
                <Button
                  size="sm"
                  onClick={handleBulkReopenWants}
                  disabled={bulkWantLoading}
                  className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full px-3 h-8 text-xs"
                >
                  {bulkWantLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                  Reopen
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setConfirmBulkDeleteWants(true)}
                disabled={bulkWantLoading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-3 h-8 text-xs"
              >
                {bulkWantLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedWantIds(new Set())}
                className="rounded-full px-3 h-8 text-xs"
              >
                Clear selection
              </Button>
            </div>
          )}

          {/* Wants List */}
          {filteredWants.length > 0 ? (
            <div className="space-y-4">
              {/* Select All Row */}
              {filteredWants.length > 1 && (
                <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                  <Checkbox
                    checked={filteredWants.length > 0 && filteredWants.every(w => selectedWantIds.has(w.id))}
                    onCheckedChange={(val) => {
                      if (val) setSelectedWantIds(new Set(filteredWants.map(w => w.id)));
                      else setSelectedWantIds(new Set());
                    }}
                    className="h-5 w-5 rounded border-2 border-[var(--theme-border-hover)] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F] data-[state=checked]:text-white"
                  />
                  <span className="text-sm text-[var(--theme-text-secondary)]">
                    {filteredWants.every(w => selectedWantIds.has(w.id)) ? 'Deselect all' : 'Select all'} ({filteredWants.length} wants)
                  </span>
                </div>
              )}

              {filteredWants.map((listing) => (
                <WantListItem
                  key={listing.id}
                  listing={listing}
                  onClick={() => navigate(`/wtb/${listing.id}`)}
                  onClose={listing.status !== 'closed' ? () => setConfirmCloseWantId(listing.id) : null}
                  onReopen={listing.status === 'closed' ? () => handleReopenWant(listing.id) : null}
                  isSelected={selectedWantIds.has(listing.id)}
                  onToggleSelect={() => toggleWantSelect(listing.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)]">
              <div className="w-16 h-16 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[#1E3A5F]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-2">
                {activeWantTab === 'closed' ? 'No Old Wants' : 'No Active Wants'}
              </h3>
              <p className="text-[var(--theme-text-secondary)] mb-6 max-w-md mx-auto">
                {activeWantTab === 'closed'
                  ? 'Wants you mark as no longer wanted will appear here.'
                  : "Create a want listing to start receiving matches from sellers."}
              </p>
              {activeWantTab !== 'closed' && (
                <Link to="/post-want">
                  <Button className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Post Your First Want
                  </Button>
                </Link>
              )}
            </div>
          )}

          </>)
        )}

      </div>

      {/* Section-agnostic Wants modals (must render regardless of active section) */}
      {confirmBulkCloseWants && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="max-w-md w-full mx-4 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#6B7280]/10 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-[#6B7280]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Mark {selectedWantIds.size} want{selectedWantIds.size !== 1 ? 's' : ''} as no longer wanted?</h2>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                  They will move to Old Wants and you'll stop receiving match notifications for them. You can reopen any time.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setConfirmBulkCloseWants(false)}
                disabled={bulkWantLoading}
                className="bg-transparent border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] rounded-full px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkCloseWants}
                disabled={bulkWantLoading}
                className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white rounded-full px-4"
              >
                {bulkWantLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
                No longer wanted
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmCloseWantId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="max-w-md w-full mx-4 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#6B7280]/10 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-[#6B7280]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Mark as no longer wanted?</h2>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                  It will move to Old Wants and you'll stop receiving match notifications for it. You can reopen it any time.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setConfirmCloseWantId(null)}
                disabled={closeWantLoading}
                className="bg-transparent border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] rounded-full px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCloseWant}
                disabled={closeWantLoading}
                className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white rounded-full px-4"
              >
                {closeWantLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
                No longer wanted
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmBulkDeleteWants && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="max-w-md w-full mx-4 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Delete {selectedWantIds.size} want{selectedWantIds.size !== 1 ? 's' : ''}?</h2>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                  This cannot be undone. Associated matches will also be removed.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setConfirmBulkDeleteWants(false)}
                disabled={bulkWantLoading}
                className="bg-transparent border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] rounded-full px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDeleteWants}
                disabled={bulkWantLoading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-4"
              >
                {bulkWantLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
